import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { apiClient } from '@/lib/api-client';
import { useHasModule, useHasPermission } from '@/auth/auth-store';
import { useFiscalCabinetAvailability } from '@/hooks/use-fiscal-cabinet';

/**
 * PFC-10 — our DPS registration data: legal entities and the fiscal side of
 * locations, terminals and cashiers, all served by the fiscal cabinet.
 */

export type LegalEntityKind = 'fop' | 'llc';
export type TaxationSystem = 'simplified' | 'general';
export type SignerRole = 'head' | 'representative';
export type RegistrationStatus = 'unregistered' | 'pending' | 'registered' | 'deregistered';
export type CashierFiscalRole = 'cashier' | 'senior_cashier';

export const REGISTRATION_STATUSES: RegistrationStatus[] = [
  'unregistered',
  'pending',
  'registered',
  'deregistered',
];

export type FiscalAddress = {
  postcode: string | null;
  region: string | null;
  district: string | null;
  settlement: string | null;
  namedObject: string | null;
  streetType: string | null;
  street: string | null;
  building: string | null;
  buildingSuffix: string | null;
  block: string | null;
  apartment: string | null;
};

export const ADDRESS_FIELDS: (keyof FiscalAddress)[] = [
  'postcode',
  'region',
  'district',
  'settlement',
  'namedObject',
  'streetType',
  'street',
  'building',
  'buildingSuffix',
  'block',
  'apartment',
];

export const EMPTY_ADDRESS: FiscalAddress = {
  postcode: null,
  region: null,
  district: null,
  settlement: null,
  namedObject: null,
  streetType: null,
  street: null,
  building: null,
  buildingSuffix: null,
  block: null,
  apartment: null,
};

export type LegalEntity = {
  id: string;
  kind: LegalEntityKind;
  tin: string;
  name: string;
  taxationSystem: TaxationSystem;
  singleTaxGroup: number | null;
  vatNumber: string | null;
  taxOfficeCode: string;
  taxOfficeRegionCode: string;
  taxOfficeDistrictCode: string;
  taxOfficeName: string;
  signerRole: SignerRole;
  signerName: string;
  signerTin: string;
  address: FiscalAddress;
  phone: string | null;
  email: string | null;
  active: boolean;
  dpsSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Stored codes the current reference data no longer lists (PFC-11). */
  referenceWarnings?: ReferenceWarning[];
};

export type LegalEntityBody = Omit<
  LegalEntity,
  | 'id'
  | 'taxOfficeRegionCode'
  | 'taxOfficeDistrictCode'
  | 'dpsSyncedAt'
  | 'createdAt'
  | 'updatedAt'
  | 'referenceWarnings'
>;

export type LegalEntityRef = { id: string; name: string; tin: string; active: boolean };

export type LocationTaxObject = {
  locationId: string;
  legalEntityId: string;
  katottg: string;
  address: FiscalAddress;
  objectTypeCode: number;
  objectName: string | null;
  stateCode: number;
  rightsCode: number;
  registerAtNonMainPlace: boolean;
  stateRegistrationNumber: string | null;
  dpsObjectCode: string | null;
  dpsRegistrationStatus: RegistrationStatus;
  dpsRegisteredAt: string | null;
  dpsSyncedAt: string | null;
  updatedAt: string;
};

export type LocationTaxObjectBody = Omit<
  LocationTaxObject,
  'locationId' | 'dpsSyncedAt' | 'updatedAt'
>;

export type LocationTaxObjectView = {
  location: { id: string; code: string; name: string };
  legalEntity: LegalEntityRef | null;
  taxObject: LocationTaxObject | null;
  referenceWarnings?: ReferenceWarning[];
};

export type TerminalPrro = {
  terminalId: string;
  fiscalNumber: string | null;
  localNumber: string;
  prroName: string;
  prroTypeCode: string;
  offlineModeCode: string;
  registrationStatus: RegistrationStatus;
  registeredAt: string | null;
  dpsSyncedAt: string | null;
  updatedAt: string;
};

export type TerminalPrroBody = Omit<TerminalPrro, 'terminalId' | 'dpsSyncedAt' | 'updatedAt'>;

export type TerminalPrroView = {
  terminal: { id: string; code: string; displayName: string };
  location: { id: string; code: string; name: string; dpsObjectCode: string | null } | null;
  legalEntity: LegalEntityRef | null;
  prro: TerminalPrro | null;
};

export type CashierFiscalProfile = {
  cashierId: string;
  legalEntityId: string;
  fullName: string;
  tin: string;
  keySerial: string | null;
  role: CashierFiscalRole;
  registrationStatus: RegistrationStatus;
  registeredAt: string | null;
  dpsSyncedAt: string | null;
  updatedAt: string;
};

export type CashierFiscalProfileBody = Omit<
  CashierFiscalProfile,
  'cashierId' | 'dpsSyncedAt' | 'updatedAt'
>;

export type CashierFiscalProfileView = {
  cashier: { id: string; employeeCode: string; displayName: string };
  legalEntity: LegalEntityRef | null;
  fiscalProfile: CashierFiscalProfile | null;
};

export type ReferenceCode<C> = { code: C; name: string };

export type FiscalReferenceCodes = {
  objectTypes: ReferenceCode<number>[];
  objectStates: ReferenceCode<number>[];
  objectRights: ReferenceCode<number>[];
  prroTypes: ReferenceCode<string>[];
  prroOfflineModes: ReferenceCode<string>[];
  versions?: Record<ReferenceDataset, ReferenceDatasetVersion>;
};

// ---------- PFC-11 reference data ----------

export type ReferenceDataset = 'taxOffices' | 'katottg' | 'objectTypes';

export type ReferenceDatasetVersion = {
  version: string;
  rows: number;
  source: { title: string; publisher: string; url: string; edition: string; retrievedAt: string };
};

export type ReferenceWarning = {
  field: 'taxOfficeCode' | 'katottg' | 'objectTypeCode';
  value: string | number;
  dataset: ReferenceDataset;
  version: string;
};

export type ReferencePage<T> = { version: string; total: number; items: T[] };

export type TaxOffice = {
  code: string;
  regionCode: string;
  districtCode: string;
  name: string;
  regionName: string;
  districtName: string;
  kind: number;
};

export type KatottgCategory = 'O' | 'K' | 'P' | 'H' | 'M' | 'X' | 'C' | 'B';

export type KatottgRef = { code: string; category: KatottgCategory; level: number; name: string };

export type KatottgUnit = KatottgRef & {
  parent: string | null;
  path: KatottgRef[];
  hasChildren: boolean;
  address: { region: string | null; district: string | null; settlement: string | null };
};

// ---------- labels (i18n key + baked English) ----------

export const KIND_LABEL_KEY: Record<LegalEntityKind, string> = {
  fop: 'fiscalCabinet.legalEntity.kind.fop',
  llc: 'fiscalCabinet.legalEntity.kind.llc',
};
export const KIND_LABEL: Record<LegalEntityKind, string> = {
  fop: 'Individual entrepreneur (FOP)',
  llc: 'Company (LLC)',
};

export const TAXATION_LABEL_KEY: Record<TaxationSystem, string> = {
  simplified: 'fiscalCabinet.legalEntity.taxation.simplified',
  general: 'fiscalCabinet.legalEntity.taxation.general',
};
export const TAXATION_LABEL: Record<TaxationSystem, string> = {
  simplified: 'Simplified (single tax)',
  general: 'General',
};

export const SIGNER_ROLE_LABEL_KEY: Record<SignerRole, string> = {
  head: 'fiscalCabinet.legalEntity.signerRole.head',
  representative: 'fiscalCabinet.legalEntity.signerRole.representative',
};
export const SIGNER_ROLE_LABEL: Record<SignerRole, string> = {
  head: 'Head / entrepreneur',
  representative: 'Authorised representative',
};

export const REGISTRATION_STATUS_LABEL_KEY: Record<RegistrationStatus, string> = {
  unregistered: 'fiscalCabinet.registration.status.unregistered',
  pending: 'fiscalCabinet.registration.status.pending',
  registered: 'fiscalCabinet.registration.status.registered',
  deregistered: 'fiscalCabinet.registration.status.deregistered',
};
export const REGISTRATION_STATUS_LABEL: Record<RegistrationStatus, string> = {
  unregistered: 'Not registered',
  pending: 'Registration pending',
  registered: 'Registered',
  deregistered: 'Deregistered',
};

export const CASHIER_ROLE_LABEL_KEY: Record<CashierFiscalRole, string> = {
  cashier: 'fiscalCabinet.cashier.role.cashier',
  senior_cashier: 'fiscalCabinet.cashier.role.seniorCashier',
};
export const CASHIER_ROLE_LABEL: Record<CashierFiscalRole, string> = {
  cashier: 'Cashier',
  senior_cashier: 'Senior cashier',
};

export const ADDRESS_LABEL_KEY: Record<keyof FiscalAddress, string> = {
  postcode: 'fiscalCabinet.address.postcode',
  region: 'fiscalCabinet.address.region',
  district: 'fiscalCabinet.address.district',
  settlement: 'fiscalCabinet.address.settlement',
  namedObject: 'fiscalCabinet.address.namedObject',
  streetType: 'fiscalCabinet.address.streetType',
  street: 'fiscalCabinet.address.street',
  building: 'fiscalCabinet.address.building',
  buildingSuffix: 'fiscalCabinet.address.buildingSuffix',
  block: 'fiscalCabinet.address.block',
  apartment: 'fiscalCabinet.address.apartment',
};
export const ADDRESS_LABEL: Record<keyof FiscalAddress, string> = {
  postcode: 'Postcode',
  region: 'Region',
  district: 'District',
  settlement: 'Settlement',
  namedObject: 'Named object (e.g. shopping centre)',
  streetType: 'Street type',
  street: 'Street',
  building: 'Building',
  buildingSuffix: 'Building suffix',
  block: 'Block',
  apartment: 'Flat / office',
};

// ---------- gate ----------

/**
 * Whether the fiscal fields belong on the location, terminal and cashier
 * pages: the tenant is entitled to the fiscal cabinet, the user may edit it
 * (`fiscal.cabinet.read` and `.write`), and the cabinet is available (UA country pack).
 *
 * The availability request is made only when the first two hold, so the
 * pages of every other tenant and user send nothing new. `useHasModule`
 * fails open while the licence list is unknown; the availability route then
 * answers 404 for an unlicensed tenant and the fields stay hidden.
 */
export function useFiscalFieldsEnabled(): boolean {
  const licensed = useHasModule('fiscal-cabinet');
  // The panel's reads need `.read`, its saves `.write`.
  const canRead = useHasPermission('fiscal.cabinet.read');
  const canWrite = useHasPermission('fiscal.cabinet.write');
  const eligible = licensed && canRead && canWrite;
  const availability = useFiscalCabinetAvailability(eligible);
  return eligible && availability.data?.available === true;
}

// ---------- queries ----------

const ROOT = ['fiscal-cabinet', 'registration'] as const;

export function useFiscalReferenceCodes(enabled = true) {
  return useQuery({
    queryKey: [...ROOT, 'reference-codes'] as const,
    enabled,
    staleTime: Infinity,
    queryFn: async (): Promise<FiscalReferenceCodes> =>
      (await apiClient.get<FiscalReferenceCodes>('/fiscal-cabinet/reference-codes')).data,
  });
}

const REFERENCE = ['fiscal-cabinet', 'reference'] as const;

/** Tax offices matching `q` (code or name); nothing is asked for a blank query. */
export function useTaxOfficeSearch(q: string, enabled = true) {
  const query = q.trim();
  return useQuery({
    queryKey: [...REFERENCE, 'tax-offices', query] as const,
    enabled: enabled && query !== '',
    staleTime: Infinity,
    queryFn: async (): Promise<ReferencePage<TaxOffice>> =>
      (
        await apiClient.get<ReferencePage<TaxOffice>>('/fiscal-cabinet/reference/tax-offices', {
          params: { q: query, limit: 10 },
        })
      ).data,
  });
}

/** One tax office by code; `null` when the directory does not list it. */
export function useTaxOffice(code: string, enabled = true) {
  return useQuery({
    queryKey: [...REFERENCE, 'tax-office', code] as const,
    enabled: enabled && /^\d{4}$/.test(code),
    staleTime: Infinity,
    queryFn: async (): Promise<{ version: string; office: TaxOffice } | null> => {
      try {
        return (
          await apiClient.get<{ version: string; office: TaxOffice }>(
            `/fiscal-cabinet/reference/tax-offices/${code}`,
          )
        ).data;
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) return null;
        throw err;
      }
    },
  });
}

/**
 * KATOTTG units: matching `q` (code or name), or — with a blank `q` — the
 * children of `parent` (the regions when there is none).
 */
export function useKatottgSearch(q: string, parent: string | null, enabled = true) {
  const query = q.trim();
  return useQuery({
    queryKey: [...REFERENCE, 'katottg', query, parent] as const,
    enabled,
    staleTime: Infinity,
    queryFn: async (): Promise<ReferencePage<KatottgUnit>> =>
      (
        await apiClient.get<ReferencePage<KatottgUnit>>('/fiscal-cabinet/reference/katottg', {
          params: {
            ...(query === '' ? {} : { q: query }),
            ...(parent === null ? {} : { parent }),
            limit: query === '' ? 100 : 20,
          },
        })
      ).data,
  });
}

/** One KATOTTG unit by code; `null` when the codifier does not list it. */
export function useKatottgUnit(code: string, enabled = true) {
  return useQuery({
    queryKey: [...REFERENCE, 'katottg-unit', code] as const,
    enabled: enabled && /^UA\d{17}$/.test(code),
    staleTime: Infinity,
    queryFn: async (): Promise<{ version: string; unit: KatottgUnit } | null> => {
      try {
        return (
          await apiClient.get<{ version: string; unit: KatottgUnit }>(
            `/fiscal-cabinet/reference/katottg/${code}`,
          )
        ).data;
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) return null;
        throw err;
      }
    },
  });
}

export function useLegalEntities(enabled = true) {
  return useQuery({
    queryKey: [...ROOT, 'legal-entities'] as const,
    enabled,
    queryFn: async (): Promise<{ items: LegalEntity[] }> =>
      (await apiClient.get<{ items: LegalEntity[] }>('/fiscal-cabinet/legal-entities')).data,
  });
}

export function useSaveLegalEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string | null; body: LegalEntityBody }) =>
      id === null
        ? (await apiClient.post<LegalEntity>('/fiscal-cabinet/legal-entities', body)).data
        : (await apiClient.put<LegalEntity>(`/fiscal-cabinet/legal-entities/${id}`, body)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROOT });
    },
  });
}

export function useLocationTaxObject(locationId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...ROOT, 'location', locationId] as const,
    enabled,
    queryFn: async (): Promise<LocationTaxObjectView> =>
      (
        await apiClient.get<LocationTaxObjectView>(
          `/fiscal-cabinet/locations/${locationId}/tax-object`,
        )
      ).data,
  });
}

export function useSaveLocationTaxObject(locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: LocationTaxObjectBody) =>
      (await apiClient.put(`/fiscal-cabinet/locations/${locationId}/tax-object`, body)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROOT });
    },
  });
}

export function useTerminalPrro(terminalId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...ROOT, 'terminal', terminalId] as const,
    enabled,
    queryFn: async (): Promise<TerminalPrroView> =>
      (await apiClient.get<TerminalPrroView>(`/fiscal-cabinet/terminals/${terminalId}/prro`)).data,
  });
}

export function useSaveTerminalPrro(terminalId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: TerminalPrroBody) =>
      (await apiClient.put(`/fiscal-cabinet/terminals/${terminalId}/prro`, body)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROOT });
    },
  });
}

export function useCashierFiscalProfile(cashierId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...ROOT, 'cashier', cashierId] as const,
    enabled,
    queryFn: async (): Promise<CashierFiscalProfileView> =>
      (
        await apiClient.get<CashierFiscalProfileView>(
          `/fiscal-cabinet/cashiers/${cashierId}/fiscal-profile`,
        )
      ).data,
  });
}

export function useSaveCashierFiscalProfile(cashierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CashierFiscalProfileBody) =>
      (await apiClient.put(`/fiscal-cabinet/cashiers/${cashierId}/fiscal-profile`, body)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROOT });
    },
  });
}
